// AI Cost Tracking API Service

import api from './api';

export interface CostToday {
  date: string;
  today_total_usd: string;
  current_hour_usd: string;
  avg_hourly_7d_usd: string;
  by_provider: Record<string, string>;
  spike_multiplier: string;
}

export interface CostSummaryPeriod {
  start_date: string;
  end_date: string;
}

export interface CostSummaryTotals {
  cost_usd: string;
  call_count: number;
  total_tokens: number;
  total_pages: number;
}

export interface CostProviderBreakdown {
  cost_usd: string;
  count: number;
}

export interface CostOperationBreakdown {
  cost_usd: string;
  count: number;
}

export interface CostSummary {
  period: CostSummaryPeriod;
  totals: CostSummaryTotals;
  by_provider: Record<string, CostProviderBreakdown>;
  by_operation: Record<string, CostOperationBreakdown>;
}

export interface DailyPoint {
  date: string | null;
  cost_usd: string;
  call_count: number;
  total_tokens: number;
}

export interface CostDaily {
  start_date: string;
  end_date: string;
  days: number;
  daily: DailyPoint[];
}

// --- Combined overview: Agent Console + API spend (new) ---

export interface WindowTotals {
  window: string;
  agent_console_usd: string;
  api_usd: string;
  combined_usd: string;
}

export interface DailyCostPoint {
  date: string;
  agent_console_usd: string;
  api_usd: string;
  combined_usd: string;
}

export interface CostOverview {
  generated_at: string;
  default_window: string;
  windows: Record<string, WindowTotals>;
  daily: DailyCostPoint[] | null;
}

export interface DoorCost {
  door_id: string;
  label: string;
  cost_usd: string;
  turn_count: number;
}

export interface AgentConsoleCostSummary {
  period: { start_date: string; end_date: string };
  total_usd: string;
  turn_count: number;
  by_door: DoorCost[];
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

export const costTrackingService = {
  getToday: async (): Promise<CostToday> => {
    const response = await api.get<CostToday>('/admin/costs/today');
    return response.data;
  },

  getSummary: async (startDate?: Date, endDate?: Date): Promise<CostSummary> => {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = formatDate(startDate);
    if (endDate) params.end_date = formatDate(endDate);
    const response = await api.get<CostSummary>('/admin/costs/summary', { params });
    return response.data;
  },

  getDaily: async (days: number = 30): Promise<CostDaily> => {
    const response = await api.get<CostDaily>('/admin/costs/daily', {
      params: { days },
    });
    return response.data;
  },

  // Combined headline (Agent Console + API) across all windows, plus the
  // 30-day stacked trend. Powers the Costs page header and the sidebar badge.
  getOverview: async (includeDaily: boolean = true): Promise<CostOverview> => {
    const response = await api.get<CostOverview>('/admin/costs/overview', {
      params: { include_daily: includeDaily },
    });
    return response.data;
  },

  // Agent Console + Co-Pilot spend for a date range, broken out by door.
  getAgentConsole: async (
    startDate: Date,
    endDate: Date,
  ): Promise<AgentConsoleCostSummary> => {
    const response = await api.get<AgentConsoleCostSummary>(
      '/admin/costs/agent-console',
      { params: { start_date: formatDate(startDate), end_date: formatDate(endDate) } },
    );
    return response.data;
  },
};
